'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Command } from 'cmdk'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { useAI } from '@/context/AIContext'
import { useAssistantPersona } from '@/context/AssistantPersonaContext'
import { useVisibleNavSections } from '@/components/layout/Sidebar'

interface CommandPaletteProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * The header search button opens the palette through this window event, so the
 * header needs no shared state with the palette (plan 03 CHG-18: "search with
 * Cmd K" in the PA header).
 */
const OPEN_EVENT = 'command-palette:open'

export function openCommandPalette() {
  window.dispatchEvent(new Event(OPEN_EVENT))
}

interface PaletteItem {
  id: string
  label: string
  icon: React.ElementType
  href?: string
  action?: 'refresh' | 'ask'
}

const ITEM_CLASS = cn(
  'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5',
  'text-sm text-foreground',
  'aria-selected:bg-brand-navy aria-selected:text-white',
  'transition-colors'
)

const GROUP_CLASS = 'px-2 py-1.5 text-xs font-semibold text-muted-foreground'

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const router = useRouter()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const { openManager } = useAI()
  const persona = useAssistantPersona()
  const sections = useVisibleNavSections()

  const controlledOpen = open !== undefined ? open : isOpen
  const setControlledOpen = onOpenChange || setIsOpen

  // Plan 03 CHG-18: the same destinations and labels as the sidebar, for this session's roles.
  const navigationItems: PaletteItem[] = sections.flatMap((section) =>
    section.items.map((item) => ({
      id: item.href,
      label: `Go to ${item.label}`,
      icon: item.icon,
      href: item.href,
    }))
  )
  const actionItems: PaletteItem[] = [
    { id: 'ask', label: `Ask ${persona.name}`, icon: Icons.messageSquare, action: 'ask' },
    { id: 'refresh', label: 'Refresh data', icon: Icons.refresh, action: 'refresh' },
  ]

  // Header search button
  React.useEffect(() => {
    const show = () => setControlledOpen(true)
    window.addEventListener(OPEN_EVENT, show)
    return () => window.removeEventListener(OPEN_EVENT, show)
  }, [setControlledOpen])

  // Keyboard shortcut handler
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to toggle
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setControlledOpen(!controlledOpen)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [controlledOpen, setControlledOpen])

  // Focus input when opened and clear search when closed
  React.useEffect(() => {
    if (controlledOpen) {
      // Small delay to ensure element is rendered
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    } else {
      setSearch('')
    }
  }, [controlledOpen])

  const handleSelect = (item: PaletteItem) => {
    setControlledOpen(false)
    setSearch('')

    if (item.href) {
      router.push(item.href)
    } else if (item.action === 'refresh') {
      window.location.reload()
    } else if (item.action === 'ask') {
      openManager()
    }
  }

  return (
    <Dialog open={controlledOpen} onOpenChange={setControlledOpen}>
      <DialogContent className='overflow-hidden p-0 sm:max-w-xl [&>button]:hidden'>
        <Command
          className={cn(
            'rounded-xl',
            'bg-white/95 backdrop-blur-xl',
            'overflow-hidden'
          )}
          loop
        >
          {/* Search Input */}
          <div className='flex items-center border-b border-border/50 px-4'>
            <Icons.search className='h-4 w-4 shrink-0 text-muted-foreground' />
            <Command.Input
              ref={inputRef}
              value={search}
              onValueChange={setSearch}
              placeholder='Type a command or search...'
              className={cn(
                'h-14 flex-1 px-3',
                'bg-transparent text-foreground',
                'placeholder:text-muted-foreground',
                'focus:outline-none'
              )}
            />
            <kbd className='hidden h-6 items-center rounded border border-border/50 bg-muted/50 px-2 text-xs text-muted-foreground sm:inline-flex'>
              ESC
            </kbd>
          </div>

          {/* Results */}
          <Command.List
            className='max-h-[300px] overflow-y-auto p-2'
            aria-live='polite'
          >
            <Command.Empty className='py-6 text-center text-sm text-muted-foreground'>
              No results found.
            </Command.Empty>

            {[
              { heading: 'Navigation', items: navigationItems },
              { heading: 'Actions', items: actionItems },
            ].map((group) => (
              <Command.Group key={group.heading} heading={group.heading} className={GROUP_CLASS}>
                {group.items.map((item) => (
                  <Command.Item
                    key={item.id}
                    value={item.label}
                    onSelect={() => handleSelect(item)}
                    className={ITEM_CLASS}
                  >
                    <item.icon className='h-4 w-4' strokeWidth={1.5} aria-hidden='true' />
                    <span>{item.label}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>

          {/* Footer */}
          <div className='flex items-center justify-between border-t border-border/50 px-4 py-2 text-xs text-muted-foreground'>
            <div className='flex items-center gap-4'>
              <span className='flex items-center gap-1'>
                <kbd className='rounded border border-border/50 bg-muted/50 px-1'>
                  ↑↓
                </kbd>
                Navigate
              </span>
              <span className='flex items-center gap-1'>
                <kbd className='rounded border border-border/50 bg-muted/50 px-1'>
                  ↵
                </kbd>
                Select
              </span>
            </div>
            <span className='flex items-center gap-1'>
              <kbd className='rounded border border-border/50 bg-muted/50 px-1'>
                ESC
              </kbd>
              Close
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  )
}

